/** Document formats accepted for upload and downstream indexing. */
export const ALLOWED_DOCUMENT_EXTENSIONS = ["pdf", "docx", "pptx"] as const;

/** Value for an <input type="file"> accept attribute — derived so the two can't drift. */
export const ACCEPTED_FILE_TYPES = ALLOWED_DOCUMENT_EXTENSIONS.map((ext) => `.${ext}`).join(",");

/** Lowercased extension without the dot, or "" when the name carries none. */
const extensionOf = (fileName: string): string =>
  fileName.includes(".") ? (fileName.split(".").pop()?.toLowerCase() ?? "") : "";

/**
 * True only for a filename ending in one of ALLOWED_DOCUMENT_EXTENSIONS. Names without an
 * extension are rejected — processFileName would otherwise turn "report" into "report.report".
 */
export const isAllowedDocumentFile = (fileName: string): boolean =>
  (ALLOWED_DOCUMENT_EXTENSIONS as readonly string[]).includes(extensionOf(fileName));

/** Office formats the backend can render to PDF for preview (via LibreOffice). */
const CONVERTIBLE_EXTENSIONS = ["docx", "pptx", "xlsx"];

/**
 * How the document viewer should obtain a preview:
 *  - "pdf"         render the file directly
 *  - "convert"     ask the API for a PDF rendering (?format=pdf), then render that
 *  - "unsupported" no preview; offer a download
 *
 * Extension-based rather than MIME-based: the API serves PPTX as application/octet-stream,
 * so the response content type can't be trusted here.
 */
export type PreviewKind = "pdf" | "convert" | "unsupported";

export const getPreviewKind = (fileName: string): PreviewKind => {
  const extension = extensionOf(fileName);
  if (extension === "pdf") return "pdf";
  if (CONVERTIBLE_EXTENSIONS.includes(extension)) return "convert";
  return "unsupported";
};

export const processFileName = (fileName: string): string => {
  const parts = fileName.split(".");
  const extension = parts.pop();
  const nameWithoutExtension = parts.join(".");
  const processedName = nameWithoutExtension
    .replaceAll(/\s+/g, "_")
    .replaceAll('.', "_")
    .replaceAll('\'', "_")
    .replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  return processedName + "." + extension;
};
